import {
  useEffect,
  useState
} from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Checkbox,
  DropdownTagSelector,
  Input,
  Modal,
  SkeletonBase,
} from '@unified-checkout/design-system';
import { MoneyBill, Plus } from '@unified-checkout/design-system/icons/index';

import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS
} from '@app/dashboard/constants/tracking_events/bills';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { DEBOUNCE_INPUT_TIME } from '@app/dashboard/constants/transfers';

import { getMerchantName } from '@app/dashboard/services/inner_layout/bills/get-merchant-name';
import { sendOTP } from '@app/dashboard/services/inner_layout/bills/send-otp';

import type {
  ITransferForm,
  ITransferModal
} from '@app/dashboard/types/innerLayout/bills/transfer-modal';

import { BalancesDetails } from '../../../../../../../components/inner_layout/BalanceDetails';
import { OTPModal } from '../OTP';

import styles from './transfer-modal.module.scss';

export const TransferModal = ({
  showTransfer,
  setShowTransfer,
  merchants = [],
  data
}: ITransferModal) => {
  const { t } = useTranslation();

  const [showOtp, setShowOtp] = useState(false);
  const [showMidInput, setShowMidInput] = useState(false);
  const [transferInputs, setTransferInputs] = useState({
    transferAmount: '',
    saveTransfer: true,
    merchantMid: '',
    selectedMerchantName: ''
  })
  const [faildSendOtp, setfaildSendOtp] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')

  const { merchantMid, selectedMerchantName, saveTransfer, transferAmount } = transferInputs;

  const {
    data: merchantNameData,
    isLoading: isMerchantNameLoading,
    isError: isMerchantNameError,
    error: merchantNameError,
    refetch
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_MERCHANT_NAME, merchantMid],
    queryFn: () => getMerchantName(merchantMid),
    enabled: false
  });

  const {
    mutate: handleSendOTP,
    isPending: isSendingOtp,
  } = useMutation({
    mutationFn: (payload: ITransferForm) => sendOTP(payload),
    onSuccess: (successData: any) => {
      setShowTransfer(false);
      setShowOtp(true);
      setPhoneNumber(successData?.data?.data?.phone_number);
      clearTransferForm();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || t('errors.somethingWentWrong');
      setfaildSendOtp(errorMessage);
    }
  })

  const hasMerchantOptions = merchants.length > 0;
  const merchantNameErrorMessage = (merchantNameError as any)?.response?.data?.message;
  const merchantName = merchantNameData?.data?.data?.company_name;
  const merchantOptions = () => merchants.map((merchant: any) => ({
    id: String(merchant?.merchant_id ?? merchant.id),
    label: merchant?.name ? merchant?.name : "",
    value: merchant,
    searchKey: merchant?.name ? merchant?.name : "",
  }))
  const selectedMerchantOption = merchantOptions().find(
    (option) => option?.id === String(merchantMid)
  );

  const sendOtpPayload = {
    receiver_merchant_id: merchantMid,
    amount: Number(transferAmount),
    save_beneficiary: !!(saveTransfer)
  };

  useEffect(() => {
    if (!merchantMid) return;

    const timeout = setTimeout(() => {
      refetch();
    }, DEBOUNCE_INPUT_TIME);

    return () => clearTimeout(timeout);
  }, [merchantMid, refetch]);

  const clearTransferForm = () => {
    setTransferInputs((prev) => ({
      ...prev,
      merchantMid: '',
      selectedMerchantId: '',
      selectedMerchantName: '',
      transferAmount: '',
      saveTransfer: true,
    }))
    setfaildSendOtp("")
    setShowMidInput(false);
  };

  const handleShowMerchantSelect = () => {
    setShowMidInput(false);
    setTransferInputs((prev) => ({
      ...prev,
      merchantMid: '',
      selectedMerchantName: '',
    }));
  };

  const handleConfirm = () => {
    setTransferInputs((prev) => ({
      ...prev,
      receiver_merchant_id: merchantMid,
      amount: Number(transferAmount),
      save_beneficiary: !!(saveTransfer)
    }))
    handleSendOTP(sendOtpPayload);

    amplitude.track(BILLS_EVENTS.BILLS_TRANSFER_CONFIRMED, {
      [BILLS_EVENTS_PARAMS.IS_SAVE_BENEFICIARY]: saveTransfer,
      [SHARED_EVENTS_PARAMS.AMOUNT]: transferAmount,
      [BILLS_EVENTS_PARAMS.RECEIPT_MID]: merchantMid
    });
  };

  const handleTransferClose = () => {
    setShowTransfer(false);
    clearTransferForm();
  };

  const isConfirmDisabled =
  !transferAmount ||
  Number(transferAmount) <= 0 ||
  Number(transferAmount) > Number(data?.amount) ||
  (!merchantMid && !selectedMerchantName) ||
  isSendingOtp ||
  (!merchantName && !selectedMerchantName);

  const MIDInput =
    <div className={styles['modal__body__merchant-wrapper']}>
      <Input
        name="MID"
        label="MID"
        type="number"
        value={merchantMid}
        status={isMerchantNameError ? 'error' : 'default'}
        placeholder="MID"
        onChange={(e) => setTransferInputs((prev) => ({
          ...prev,
          merchantMid: e.target.value,
        }))}
        error={isMerchantNameError ? merchantNameErrorMessage : ''}
      />
      {
        merchantName ?
          <div className={styles['modal__body__merchant']}>
            <div className={styles['modal__body__merchant__label']}>
              {t('innerLayout.billsPage.transferModal.merchantName')}
            </div>

            <div className={styles['modal__body__merchant__value']}>
              {merchantName || '-'}
            </div>
          </div>
          :
          isMerchantNameLoading &&
          <div className={styles['modal__body__merchant']}>
            <SkeletonBase
              height='1rem'
              width='8rem'
            />
          </div>
      }

      {hasMerchantOptions && (
        <Button
          text={t('innerLayout.billsPage.transferModal.selectSavedMerchant')}
          variant="link"
          size='md'
          color='primary'
          onClickHandler={handleShowMerchantSelect}
        />
      )}
    </div>

  const dropDownMerchantOptions =
    <div className={styles['modal__body__dropdown']}>
      <DropdownTagSelector
        isMultiple={false}
        isResetSearchQueryOnToggle={false}

        dropdownProps={{
          label: t('innerLayout.billsPage.transferModal.selectMerchant'),
        }}
        optionsList={merchantOptions()}
        selectedOptions={selectedMerchantOption ? [selectedMerchantOption] : []}
        onSelectionChange={(items) => {
          setTransferInputs((prev) => ({
            ...prev,
            selectedMerchantName: items[0]?.label,
            merchantMid: items[0]?.id,
          }));
        }}
        tagsInputProps={{
          name: 'bills-transfer-merchant',
          placeholder: t('innerLayout.billsPage.transferModal.selectMerchantMid'),
        }}
      />
      <Button
        text={t('innerLayout.billsPage.transferModal.addNewMerchant')}
        variant='link'
        size='md'
        color='primary'
        leadingIcon={Plus}
        onClickHandler={() => setShowMidInput(true)}
      />
    </div>

  return (
    <div className={styles['modal']}>
      <Modal
        state="primary"
        headerIcon={MoneyBill}
        hasCloseIcon
        open={showTransfer}
        position='center'
        onCloseHandler={handleTransferClose}
        title={t('innerLayout.billsPage.transferModal.title')}
        errorMsg={faildSendOtp}
        btnsProps={[
          {
            text: t('innerLayout.billsPage.transferModal.confirmButton'),
            onClickHandler: handleConfirm,
            disabled: isConfirmDisabled,
            loading: isSendingOtp
          },
          {
            color: "secondary" as const,
            variant: "outlined" as const,
            text: t('innerLayout.billsPage.transferModal.cancelButton'),
            onClickHandler: handleTransferClose,
          }]}
      >
        <div className={styles['modal__body']}>
          <BalancesDetails
            title={t('innerLayout.billsPage.billsBalance')}
            amount={data?.amount}
            currency={data?.currency}
          />
          {hasMerchantOptions && !showMidInput ? dropDownMerchantOptions : MIDInput}

          <Input
            name="bills-transfer-amount"
            label={t("innerLayout.billsPage.transferModal.amount")}
            type="number"
            value={transferAmount}
            status={Number(transferAmount) < 0 ? 'error' : 'default'}
            placeholder="0.00"
            onChange={(e) => setTransferInputs((prev) => ({
              ...prev,
              transferAmount: e.target.value,
            }))}
          />

          <Checkbox
            type="checkbox"
            id="bills-transfer-save"
            text={t("innerLayout.billsPage.transferModal.saveBeneficiary")}
            checked={saveTransfer}
            onClickHandler={(checked) => setTransferInputs((prev) => ({
              ...prev,
              saveTransfer: checked
            }))}
          />
        </div>
      </Modal>
      <OTPModal
        showOtp={showOtp}
        setShowOtp={setShowOtp}
        transferInputs={transferInputs}
        resendOtp={handleSendOTP}
        phoneNumber={phoneNumber}
      />
    </div>
  );
};